import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';
import type {
  CreateReleaseNotePayload,
  ListReleaseNotesParams,
  ReleaseNoteDto,
  UpdateReleaseNotePayload,
} from '../models/release-notes.models';

function toQuery(params: ListReleaseNotesParams): string {
  const q = new URLSearchParams();
  if (params.search?.trim()) q.set('search', params.search.trim());
  const s = q.toString();
  return s ? `?${s}` : '';
}

@Injectable({ providedIn: 'root' })
export class ReleaseNotesService {
  private readonly api = inject(ApiService);

  listPlatform(params: ListReleaseNotesParams = {}) {
    return this.api
      .get<ApiEnvelope<ReleaseNoteDto[]>>(`platform/release-notes${toQuery(params)}`)
      .pipe(map((r) => r.data));
  }

  listPublic(params: ListReleaseNotesParams = {}) {
    return this.api
      .get<ApiEnvelope<ReleaseNoteDto[]>>(`public/release-notes${toQuery(params)}`)
      .pipe(map((r) => r.data));
  }

  getPlatform(id: string) {
    return this.api
      .get<ApiEnvelope<ReleaseNoteDto>>(`platform/release-notes/${id}`)
      .pipe(map((r) => r.data));
  }

  create(payload: CreateReleaseNotePayload) {
    return this.api
      .post<ApiEnvelope<ReleaseNoteDto>>('platform/release-notes', payload)
      .pipe(map((r) => r.data));
  }

  update(id: string, payload: UpdateReleaseNotePayload) {
    return this.api
      .patch<ApiEnvelope<ReleaseNoteDto>>(`platform/release-notes/${id}`, payload)
      .pipe(map((r) => r.data));
  }

  duplicate(id: string) {
    return this.api
      .post<ApiEnvelope<ReleaseNoteDto>>(`platform/release-notes/${id}/duplicate`, {})
      .pipe(map((r) => r.data));
  }

  publish(id: string) {
    return this.api
      .patch<ApiEnvelope<ReleaseNoteDto>>(`platform/release-notes/${id}/publish`, {})
      .pipe(map((r) => r.data));
  }

  unpublish(id: string) {
    return this.api
      .patch<ApiEnvelope<ReleaseNoteDto>>(`platform/release-notes/${id}/unpublish`, {})
      .pipe(map((r) => r.data));
  }

  remove(id: string) {
    return this.api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`platform/release-notes/${id}`)
      .pipe(map((r) => r.data));
  }
}
